import {Trans, useTranslation} from "react-i18next";
import React, {useEffect, useMemo, useRef, useState} from "react";
import ToolbarContainer from "../../EditProduct/Components/ToolbarContainer";
import ToolbarHeader from "../../EditProduct/Components/ToolbarHeader";
import ToolbarContent from "../../EditProduct/Components/ToolbarContent";
import {Bubble, BubbleList} from "../../../Components/EditTemplateProduct/Components/Bubble";
import Icon from "@material-ui/core/Icon";
import ActionsContainer from "../../EditProduct/Components/ActionsContainer";
import Button from "@material-ui/core/Button";
import {usePriceList} from "../../../../Providers/PriceListProvider";
import {ToolbarOption, ToolbarOptions} from "../../EditProduct/Components/ToolBarOptions";
import {makeStyles} from "@material-ui/core/styles";
import Box from "@material-ui/core/Box";
import {ACTIONS} from "../ShoppingCartPage";

const useStyles = makeStyles(theme => ({
    selectedFormat: {
        backgroundColor: '#BFDFEC',
        color: 'black',
        '&:active, &:focus, &:hover': {
            backgroundColor: '#BFDFEC',
            color: 'black',
        }
    },
    formatFeatures: {
        fontSize: '0.8em',
        textTransform: 'none',
        fontWeight: 'normal',
        whiteSpace: 'break-spaces'
    },
    quantityContainer: {
        display: 'flex',
        justifyContent: 'center',
        padding: 0,
        margin: 0,
        listStyle: 'none',
        marginBottom: theme.spacing(2),
    },
    quantityButton: {
        width: '64px',
        height: '64px'
    },
    inputQuantity: {
        width: '64px',
        height: '64px',
        border: '2px solid ' + theme.palette.primary.main,
        boxSizing: 'border-box',
        fontSize: '24px',
        textAlign: 'center',
        lineHeight: '64px',
        marginLeft: theme.spacing(2),
        marginRight: theme.spacing(2)
    },
    editImageToolList: {
        display: 'flex',
        justifyContent: 'space-around',
        listStyle: 'none',
        margin: 0,
        padding: 0,
        '& > li': {
            display: 'inline-block'
        },
        '& > li:first-child': {
            marginRight: theme.spacing(1)
        },
        '& > li:last-child': {
            marginLeft: theme.spacing(1)
        },
        '& button': {
            fontSize: '12px',
            display: 'flex',
            flexDirection: 'column',
            color: theme.palette.getContrastText(theme.palette.primary.main),
            backgroundColor: theme.palette.primary.main,
            width: '76px',
            height: '74px',
            border: 'none',
            borderBottom: '2px solid ' + theme.palette.primary.main,
            '&.selected': {
                borderBottom: '2px solid ' + theme.palette.secondary.main,
            },
            '& > div': {
                width: '100%',
                flexGrow: 1,
                verticalAlign: 'center',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
            },
            '& > div:last-child': {
                alignItems: 'flex-start'
            }
        }
    },
}));

const MultiSelect = ({selectedItems, onClose, orderLines, dispatch, handleUpdate, paperCopyItems}) => {
    const [t] = useTranslation();
    const [state, setState] = useState('main')

    const [selectedOrderLines, qtys, formats] = useMemo(() => {
        const ols = [];
        for (const uuid of selectedItems) {
            const ol = orderLines.find(ol => ol.uuid === uuid);
            if (ol) {
                ols.push(ol);
            }
        }

        const qtys = [];
        const formats = [];
        for (const ol of ols) {
            if (qtys.indexOf(ol.quantity) < 0) {
                qtys.push(ol.quantity);
            }
            const format = formats.find(f => f.uuid == ol.product.uuid);
            if (!format) {
                formats.push(ol.product);
            }
        }

        return [ols, qtys, formats];
    }, [selectedItems, orderLines]);


    switch (state) {
        case 'main':
            return <MainMenu orderLines={selectedOrderLines} selectedItems={selectedItems} onClose={onClose}
                             qtys={qtys} formats={formats} handleUpdate={handleUpdate} paperCopyItems={paperCopyItems}
                             setMenu={(menu) => setState(menu)} dispatch={dispatch}/>
        case 'format':
            return <FormatMenu orderLines={selectedOrderLines} onClose={onClose} setMenu={(menu) => setState(menu)}
                               formats={formats}  dispatch={dispatch}/>
        case 'quantity':
            return <QuantityMenu orderLines={selectedOrderLines} onClose={onClose} setMenu={(menu) => setState(menu)}
                                 formats={formats} dispatch={dispatch}/>
        case 'filter':
            return <FilterMenu orderLines={selectedOrderLines} onClose={onClose} setMenu={(menu) => setState(menu)}
                               formats={formats} dispatch={dispatch}/>
    }
}


const MainMenu = ({onClose, orderLines, selectedItems, setMenu, formats, qtys, handleUpdate, dispatch, paperCopyItems}) => {
    const [t] = useTranslation();

    const handleDelete = () => {
        dispatch({
            type: ACTIONS.TOGGLE_DELETED,
            orderLines: orderLines,
        })
    }

    const handleCancel = () => {
        dispatch({
            type: ACTIONS.SET_ITEMS,
            items: paperCopyItems
        })
        onClose();
    }


    return <ToolbarContainer>
        <ToolbarHeader>
            {t('%d selected pictures', {postProcess: 'sprintf', sprintf: [selectedItems.length]})}
        </ToolbarHeader>
        <ToolbarContent>
            <BubbleList>
                <Bubble
                    header={t('Format')}
                    content={formats.length == 1 ? <div>{formats[0].name}</div> : <Trans>Select</Trans>}
                    onClick={() => setMenu('format')}/>
                <Bubble
                    header={t('Filter')}
                    content={<Icon>filter</Icon>}
                    onClick={() => setMenu('filter')}/>
                <Bubble
                    header={t('Quantity')}
                    content={qtys.length == 1 ? <div>{qtys[0]}</div> : <Trans>Select</Trans>}
                    onClick={() => setMenu('quantity')}/>
                <Bubble
                    header={t('Delete')}
                    content={<Icon>delete</Icon>}
                    onClick={handleDelete}/>
            </BubbleList>
        </ToolbarContent>
        <ActionsContainer>
            <Button color="primary" onClick={handleCancel}><Trans>Cancel</Trans></Button>
            <Button color="primary" onClick={handleUpdate}><Trans>Done</Trans></Button>
        </ActionsContainer>
    </ToolbarContainer>
}

const FormatMenu = ({ orderLines, setMenu, formats, dispatch}) => {
    const [t] = useTranslation();
    const {getPrintFormats, getProductByUuid} = usePriceList();
    const classes = useStyles();
    const [localFormats, setLocalFormats] = useState(formats.map(format => format.uuid));

    const printFormats = useMemo(() => {
        return getPrintFormats()
    }, [])


    const handleSetFormat = () => {
        if (localFormats.length == 1) {
            const format = getProductByUuid(localFormats[0])
            dispatch({
                type: ACTIONS.SET_FORMAT,
                orderLines: orderLines,
                format: format
            })
        }
        setMenu('main')
    }


    console.log('Local formats', localFormats);

    return <ToolbarContainer>
        <ToolbarHeader>
            {t('%d selected pictures', {postProcess: 'sprintf', sprintf: [orderLines.length]})}
        </ToolbarHeader>
        <ToolbarContent>
            <ToolbarOptions header={t('Format')}>
                {printFormats.map(format => {
                    let nameParts = format.name.match(/^(\d+x\d+)[\s]{0,1}(.*)$/);
                    if (!nameParts) {
                        nameParts = ['', format.name];
                    }

                    const isSelected = (localFormats.indexOf(format.uuid) >= 0);
                    console.log('Local formats', isSelected, format.uuid);

                    const content = <>
                        <div className='format-name'>{nameParts[1]}</div>
                        {nameParts.length > 2 && <div className={classes.formatFeatures}>{nameParts[2]}</div>}
                    </>

                    return <ToolbarOption key={format.uuid}
                                          content={content}
                                          onClick={() => setLocalFormats([format.uuid])}
                                          selected={isSelected}/>
                })}
            </ToolbarOptions>
        </ToolbarContent>
        <ActionsContainer>
            <Button color="primary" onClick={() => setMenu('main')}><Trans>Cancel</Trans></Button>
            <Button color="primary" onClick={handleSetFormat} disabled={localFormats.length == 0}><Trans>Done</Trans></Button>
        </ActionsContainer>
    </ToolbarContainer>
}

const QuantityMenu = ({orderLines, setMenu, formats, dispatch}) => {
    const [t] = useTranslation();
    const classes = useStyles();
    const {getProductByUuid} = usePriceList();

    // We have to get the max minimum quantity
    const minimumQuantity = useMemo(() => {
        let minQuantity = 1;
        console.log('Formats', formats);
        for (const format of formats) {
            const formatMinQuantity = parseInt(getProductByUuid(format.uuid).minorderpartquantity)
            if (formatMinQuantity > minQuantity) {
                minQuantity = formatMinQuantity;
            }
        }
        console.log('Min qty', minQuantity)
        return minQuantity;
    }, [])

    const [currentQuantity, setCurrentQuantity] = useState(() => {
        return orderLines.reduce((max, ol) => {
            if (ol.quantity > max) {
                return ol.quantity;
            }
            return max;
        }, 1);
    });


    const decreaseQuantity = () => {
        setCurrentQuantity((currentQuantity => {
            let qty = currentQuantity - 1;
            if (qty < minimumQuantity) {
                qty = minimumQuantity;
            }
            return qty;
        }))
    }

    const increaseQuantity = () => {
        setCurrentQuantity(currentQuantity + 1)
    }

    const onChange = e => {
        setCurrentQuantity(e.target.value);
    }

    const onBlur = e => {
        if (isNaN(e.target.value) || parseInt(currentQuantity) < minimumQuantity) {
            setCurrentQuantity(minimumQuantity)
        } else {
            setCurrentQuantity(e.target.value);
        }
    }

    const handleSetQuantity = () => {
        dispatch({
            type: ACTIONS.SET_QUANTITY,
            orderLines: orderLines,
            quantity: currentQuantity
        })

        setMenu('main')
    }


    return <ToolbarContainer>
        <ToolbarHeader>
            {t('%d selected pictures', {postProcess: 'sprintf', sprintf: [orderLines.length]})}
        </ToolbarHeader>
        <ToolbarContent>
            <Box display="flex" justifyContent="center" alignItems="center" width="100%" height="100%">
                <ul className={classes.quantityContainer}>
                    <li>
                        <Button variant='contained' className={classes.quantityButton} color="primary"
                                onClick={e => decreaseQuantity()}>
                            -
                        </Button>
                    </li>
                    <li>
                        <input type="text" value={currentQuantity} className={classes.inputQuantity} onChange={onChange}
                               onBlur={onBlur}/>
                    </li>
                    <li>
                        <Button variant='contained' className={classes.quantityButton} color="primary"
                                onClick={e => increaseQuantity()}>
                            +
                        </Button>
                    </li>
                </ul>
            </Box>
        </ToolbarContent>
        <ActionsContainer>
            <Button color="primary" onClick={() => setMenu('main')}><Trans>Cancel</Trans></Button>
            <Button color="primary" onClick={handleSetQuantity}><Trans>Done</Trans></Button>
        </ActionsContainer>
    </ToolbarContainer>
}

const FilterMenu = ({orderLines, setMenu, dispatch}) => {
    const [t] = useTranslation();
    const classes = useStyles();
    const [filter, handleFilter] = useState(() => {
        // Get unique filters
        const filters = orderLines.map(ol => ol.image.filter)
            .filter((item, i, ar) => ar.indexOf(item) === i);
        if (filters.length == 1) {
            return filters[0]
        }
        return '';
    })

    const handleSetFilter = () => {
        if (filter != '') {
            dispatch({
                type: ACTIONS.SET_FILTER,
                orderLines: orderLines,
                filter: filter,
            })
        }
        setMenu('main')
    }

    return <ToolbarContainer>
        <ToolbarHeader>
            {t('%d selected pictures', {postProcess: 'sprintf', sprintf: [orderLines.length]})}
        </ToolbarHeader>
        <ToolbarContent>
            <Box display="flex" justifyContent="center" alignItems="center" width="100%" height="100%">
                <ul className={classes.editImageToolList}>
                    <li>
                        <ImageTool heading={t('Black/White')} icon={<Icon>filter</Icon>}
                                   selected={filter == 'bw'}
                                   onClick={() => handleFilter('bw')}/>
                    </li>
                    <li>
                        <ImageTool heading={t('Sepia')} icon={<Icon>filter</Icon>}
                                   selected={filter == 'sepia'}
                                   onClick={() => handleFilter('sepia')}/>
                    </li>
                    <li>
                        <ImageTool heading={t('Original')} icon={<Icon>filter</Icon>}
                                   selected={filter == 'original'}
                                   onClick={() => handleFilter('original')}/>
                    </li>
                </ul>
            </Box>
        </ToolbarContent>
        <ActionsContainer>
            <Button color="primary" onClick={() => setMenu('main')}><Trans>Cancel</Trans></Button>
            <Button color="primary" onClick={handleSetFilter}><Trans>Done</Trans></Button>
        </ActionsContainer>
    </ToolbarContainer>
}

const ImageTool = ({heading, icon, onClick, selected}) => {
    return <button onClick={onClick} className={selected ? 'selected' : ''}>
        <div>{heading}</div>
        <div>{icon}</div>
    </button>
}


export default MultiSelect;